// src/features/recipes/RecipeDetailScreen.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  Skeleton,
  Button,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getRecipeById, generateRecipeImage, addRecipeToPlan } from "../../services/api";
import { Restaurant, CheckCircle, SwapHoriz, AddCircle } from "@mui/icons-material";
import api from "../../services/api";
import dayjs from "dayjs";
import { trackEvent, AnalyticsEvents } from "../../services/analytics";

interface NutritionInfo {
  per_serving?: {
    calories?: number;
    protein_grams?: number;
    carbs_grams?: number;
    fat_grams?: number;
    fiber_grams?: number;
    sodium_mg?: number;
  };
  total_servings?: number;
  metadata?: {
    difficulty?: string;
    total_time_minutes?: number;
    prep_time_minutes?: number;
    cook_time_minutes?: number;
    cuisine_type?: string;
    course?: string;
    dietary_tags?: string[];
  };
}

// Component for displaying recipe metadata
const RecipeMetadata: React.FC<{
  nutrition?: NutritionInfo;
  prepTime?: number;
  mealServings?: number; // Override from meal data (servings + extra_servings)
}> = ({ nutrition, prepTime, mealServings }) => {
  const perServing = nutrition?.per_serving;
  const metadata = nutrition?.metadata;
  // Use meal's serving count if available (more accurate), otherwise fall back to recipe's cached value
  const servings = mealServings || nutrition?.total_servings;
  
  return (
    <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Recipe Information
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
        {/* Basic Info */}
        <Box>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            <strong>Recipe Details</strong>
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Serves: <strong>{servings || 'N/A'}</strong>
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Prep Time: <strong>{metadata?.prep_time_minutes || prepTime || 'N/A'} min</strong>
          </Typography>
          {metadata?.cook_time_minutes && (
            <Typography variant="body1" color="text.secondary">
              Cook Time: <strong>{metadata.cook_time_minutes} min</strong>
            </Typography>
          )}
          {metadata?.total_time_minutes && (
            <Typography variant="body1" color="text.secondary">
              Total Time: <strong>{metadata.total_time_minutes} min</strong>
            </Typography>
          )}
          {metadata?.difficulty && (
            <Typography variant="body1" color="text.secondary">
              Difficulty: <strong>{metadata.difficulty}</strong>
            </Typography>
          )}
          {metadata?.cuisine_type && (
            <Typography variant="body1" color="text.secondary">
              Cuisine: <strong>{metadata.cuisine_type}</strong>
            </Typography>
          )}
        </Box>
        
        {/* Nutrition Info */}
        {perServing && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Nutrition Per Serving</strong>
            </Typography>
            {perServing.calories && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Calories: <strong>{perServing.calories}</strong>
              </Typography>
            )}
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1 }}>
              {perServing.protein_grams && (
                <Typography variant="body2" color="text.secondary">
                  Protein: <strong>{perServing.protein_grams}g</strong>
                </Typography>
              )}
              {perServing.carbs_grams && (
                <Typography variant="body2" color="text.secondary">
                  Carbs: <strong>{perServing.carbs_grams}g</strong>
                </Typography>
              )}
              {perServing.fat_grams && (
                <Typography variant="body2" color="text.secondary">
                  Fat: <strong>{perServing.fat_grams}g</strong>
                </Typography>
              )}
              {perServing.fiber_grams && (
                <Typography variant="body2" color="text.secondary">
                  Fiber: <strong>{perServing.fiber_grams}g</strong>
                </Typography>
              )}
            </Box>
            
            {perServing.sodium_mg && (
              <Typography variant="body2" color="text.secondary">
                Sodium: <strong>{perServing.sodium_mg}mg</strong>
              </Typography>
            )}
          </Box>
        )}
      </Box>
      
      {/* Dietary Tags */}
      {metadata?.dietary_tags && metadata.dietary_tags.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Dietary Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {metadata.dietary_tags.map((tag, idx) => (
              <Chip 
                key={idx} 
                label={tag} 
                size="small" 
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const RecipeDetailScreen: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mealId = searchParams.get('meal_id'); // Get meal_id from URL params
  const [recipe, setRecipe] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [mealData, setMealData] = useState<any | null>(null);
  const [planId, setPlanId] = useState<number | null>(null);

  // Add to Plan dialog state
  const [addToPlanOpen, setAddToPlanOpen] = useState(false);
  const [addToPlanDate, setAddToPlanDate] = useState<string>('');
  const [addToPlanMealType, setAddToPlanMealType] = useState<string>('dinner');
  const [addingToPlan, setAddingToPlan] = useState(false);
  const [addToPlanSuccess, setAddToPlanSuccess] = useState<string | null>(null);
  const [addToPlanError, setAddToPlanError] = useState<string | null>(null);

  const getDateOptions = () => {
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : `${dayNames[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
      days.push({ date: dateStr, label });
    }
    return days;
  };

  const handleAddToPlan = async () => {
    if (!recipe || !addToPlanDate) return;
    setAddingToPlan(true);
    setAddToPlanError(null);
    try {
      const result = await addRecipeToPlan({
        recipe_id: recipe.recipe_id,
        meal_type: addToPlanMealType,
        assigned_date: addToPlanDate,
      });
      setAddToPlanOpen(false);
      setAddToPlanSuccess(`Added ${recipe.name} for ${addToPlanMealType} on ${addToPlanDate} (${result.servings} servings).`);
    } catch (err: any) {
      setAddToPlanError(err.response?.data?.message || 'Failed to add recipe to plan.');
    } finally {
      setAddingToPlan(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getRecipeById(Number(id), mealId ? Number(mealId) : undefined);
        setRecipe(data);
        trackEvent(AnalyticsEvents.RECIPE_VIEWED, { recipe_id: Number(id), recipe_name: data?.name });

        // Load meal data if meal_id is provided (for leftover status, plan link, etc.)
        if (mealId) {
          try {
            const mealResponse = await api.get(`/meals/${mealId}`);
            setMealData(mealResponse.data);
            if (mealResponse.data.plan_id) {
              setPlanId(mealResponse.data.plan_id);
            }
          } catch (err) {
            console.error('Failed to load meal data', err);
          }
        }

        // Lazy load image if it doesn't exist
        if (!data.image_url && !imageLoading) {
          setImageLoading(true);
          try {
            const imageData = await generateRecipeImage(Number(id));
            if (imageData.success && imageData.image_url) {
              setRecipe((prev: any) => ({
                ...prev,
                image_url: imageData.image_url,
                image_photographer: imageData.image_photographer,
                image_photographer_url: imageData.image_photographer_url
              }));
            }
          } catch (imgErr) {
            console.error("Failed to generate image", imgErr);
          } finally {
            setImageLoading(false);
          }
        }
      } catch (err) {
        console.error("Failed to load recipe", err);
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mealId]);

  if (loading) {
    return (
      <Box mt={6} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!recipe) {
    return (
      <Box mt={6} textAlign="center">
        <Typography>Recipe not found.</Typography>
      </Box>
    );
  }

  const hasFullDetails = recipe.ingredients && recipe.ingredients.length > 0;

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" gutterBottom>
        {recipe.name}
      </Typography>

      <Typography variant="subtitle1" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
        {recipe.description}
      </Typography>

      {/* Alert for incomplete recipe */}
      {!hasFullDetails && planId && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Recipe Details Not Available Yet</AlertTitle>
          This recipe hasn't been fully expanded with ingredients and cooking instructions yet.
          Please approve your meal plan to generate complete recipe details.
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => navigate(`/meal-plans/${planId}/review`)}
            >
              Review & Approve Meal Plan
            </Button>
          </Box>
        </Alert>
      )}

      {/* Recipe Metadata */}
      <RecipeMetadata
        nutrition={recipe.nutrition_info}
        prepTime={recipe.prep_time}
        mealServings={mealData ? (mealData.servings + (mealData.extra_servings || 0)) : undefined}
      />

      {/* Recipe Hero Image */}
      {imageLoading ? (
        <Box sx={{ width: "100%", my: 3 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ borderRadius: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Generating image...
          </Typography>
        </Box>
      ) : (recipe.image_url || recipe.images?.[0]?.image_url) && (
        <Box sx={{ width: "100%", my: 3, position: 'relative' }}>
          <Box
            component="img"
            src={recipe.image_url || recipe.images?.[0]?.image_url}
            alt={recipe.name}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src =
                "https://via.placeholder.com/800x400?text=Recipe+Image";
            }}
            sx={{
              width: "100%",
              maxHeight: 500,
              objectFit: "cover",
              borderRadius: 2,
              boxShadow: 3
            }}
          />
          {/* Pexels Attribution */}
          {recipe.image_photographer && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: 1,
                fontSize: '11px'
              }}
            >
              📷{' '}
              {recipe.image_photographer_url ? (
                <a
                  href={recipe.image_photographer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'white', textDecoration: 'none' }}
                >
                  {recipe.image_photographer}
                </a>
              ) : (
                recipe.image_photographer
              )}{' '}
              via Pexels
            </Box>
          )}
        </Box>
      )}

      {/* Leftover Reheat Instructions OR Full Recipe */}
      {mealData?.is_leftover ? (
        <>
          {/* Reheat Instructions for Leftovers */}
          <Alert
            severity="info"
            icon={<span style={{ fontSize: '1.5rem' }}>🍱</span>}
            sx={{ mb: 3 }}
          >
            <AlertTitle>This is a Leftover Meal</AlertTitle>
            <Typography variant="body2">
              No cooking required! Just reheat {mealData.servings} serving{mealData.servings !== 1 ? 's' : ''} from your previous dinner.
            </Typography>
          </Alert>

          <Typography variant="h5" sx={{ mb: 2 }}>
            Reheating Options
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                🔥 Microwave (Fastest)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. Transfer to a microwave-safe container<br/>
                2. Cover loosely with a damp paper towel<br/>
                3. Heat for 2-3 minutes, stirring halfway through<br/>
                4. Let stand 1 minute before serving
              </Typography>
            </Box>

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                🍳 Stovetop (Best Quality)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. Add a splash of water or broth to a pan<br/>
                2. Heat over medium-low heat<br/>
                3. Add food and cover<br/>
                4. Stir occasionally until heated through (5-7 minutes)
              </Typography>
            </Box>

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                🌡️ Oven (For Crispy Textures)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1. Preheat oven to 350°F (175°C)<br/>
                2. Place food in an oven-safe dish<br/>
                3. Cover with foil to prevent drying<br/>
                4. Heat for 15-20 minutes until warmed through
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Chip
              icon={<CheckCircle />}
              label={`Reheat ${mealData.servings} serving${mealData.servings !== 1 ? 's' : ''} and enjoy!`}
              color="info"
              sx={{ fontSize: '1rem', py: 2.5, px: 2 }}
            />
          </Box>
        </>
      ) : (
        <>
          {/* Full Recipe for Non-Leftovers */}
          {recipe.instructions && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Overview
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                {recipe.instructions}
              </Typography>
            </Box>
          )}

          {/* Modular recipe: protein selections */}
          {mealData?.protein_selections?.length > 0 && (
            <Alert severity="info" icon={<Restaurant />} sx={{ mb: 3 }}>
              <AlertTitle>Protein Options</AlertTitle>
              {mealData.protein_selections.map((ps: any, i: number) => (
                <Box key={i} sx={{ mb: i < mealData.protein_selections.length - 1 ? 1 : 0 }}>
                  <Typography variant="subtitle2" color="primary">{ps.member_name}</Typography>
                  <Typography variant="body2">
                    {ps.option_name} — {ps.quantity_per_serving} {ps.unit}
                  </Typography>
                </Box>
              ))}
            </Alert>
          )}

          {/* Structured adaptations (non-modular mixed-diet) */}
          {mealData?.adaptations?.length > 0 && (
            <Alert severity="info" icon={<SwapHoriz />} sx={{ mb: 3 }}>
              <AlertTitle>Adaptations</AlertTitle>
              {mealData.adaptations.map((adapt: any, i: number) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" color="primary">
                    {adapt.members.join(' & ')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {adapt.action === 'replace'
                      ? `${adapt.substitute} instead of ${adapt.original}`
                      : adapt.action === 'add'
                      ? `Add ${adapt.substitute}`
                      : `Remove ${adapt.original}`}
                    {adapt.quantity ? ` (${adapt.quantity})` : ''}
                  </Typography>
                </Box>
              ))}
            </Alert>
          )}

          {/* Legacy fallback: plain notes + ingredient overrides */}
          {!mealData?.adaptations?.length && !mealData?.protein_selections?.length &&
           (mealData?.notes || (mealData?.ingredient_overrides && mealData.ingredient_overrides.length > 0)) && (
            <Alert severity="info" icon={<SwapHoriz />} sx={{ mb: 3 }}>
              <AlertTitle>Meal Adaptations</AlertTitle>
              {mealData.notes && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {mealData.notes}
                </Typography>
              )}
              {mealData.ingredient_overrides?.length > 0 && (
                <List dense sx={{ mt: 1 }}>
                  {mealData.ingredient_overrides.map((ov: any, i: number) => (
                    <ListItem key={i} disablePadding>
                      <ListItemText
                        primary={
                          ov.action === 'add' ? `+ Add ${ov.quantity || ''} ${ov.unit || ''} ${ov.ingredient}`.trim()
                          : ov.action === 'remove' ? `− Remove ${ov.ingredient}`
                          : `↕ Scale ${ov.ingredient}`
                        }
                        secondary={ov.reason}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Alert>
          )}

          <Typography variant="h5" sx={{ mb: 2 }}>
            Ingredients
            {recipe.meal_context && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                (for {recipe.meal_context.total_servings} servings)
              </Typography>
            )}
          </Typography>
          <List sx={{ bgcolor: 'background.paper', borderRadius: 1, mb: 4 }}>
            {recipe.ingredients?.filter((ing: any) => !ing.is_removed).map((ing: any, idx: number) => {
              const displayQty = ing.display_quantity ?? ing.scaled_quantity ?? ing.quantity;
              const displayUnit = ing.display_unit ?? ing.unit;
              return (
                <ListItem key={idx} dense>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={ing.is_added ? { color: 'success.main' } : undefined}>
                        {ing.is_added ? '+ ' : ''}
                        <strong>{displayQty ?? ""} {displayUnit ?? ""}</strong> {ing.name}
                        {ing.is_added && ing.reason ? (
                          <Typography component="span" variant="body2" color="text.secondary"> ({ing.reason})</Typography>
                        ) : null}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 4 }} />

          {/* Cooking Button */}
          <Box sx={{ textAlign: 'center', my: 4 }}>
            {mealData?.status === 'completed' ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => navigate(`/recipe/${id}/cook${mealId ? `?meal_id=${mealId}` : ''}`)}
                  sx={{
                    py: 2,
                    px: 6,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: 3,
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 6,
                    }
                  }}
                >
                  View Recipe Steps
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  ✓ Cooked on {dayjs(mealData.completed_at).format('MMM D, YYYY [at] h:mm A')}
                </Typography>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Restaurant />}
                  onClick={() => navigate(`/recipe/${id}/cook${mealId ? `?meal_id=${mealId}` : ''}`)}
                  sx={{
                    py: 2,
                    px: 6,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: 3,
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 6,
                    }
                  }}
                >
                  Let's Cook!
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {mealData?.extra_servings > 0
                    ? `Cook ${(mealData.servings || 0) + mealData.extra_servings} servings (+${mealData.extra_servings} for leftovers)`
                    : 'Start step-by-step cooking mode'
                  }
                </Typography>
              </>
            )}
          </Box>

          {/* Add to Plan button — only when browsing (not viewing a meal that's already on plan) */}
          {!mealId && recipe?.ingredients?.length > 0 && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<AddCircle />}
                onClick={() => {
                  setAddToPlanDate(getDateOptions()[0].date);
                  setAddToPlanOpen(true);
                }}
              >
                Add to Meal Plan
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Success snackbar */}
      {addToPlanSuccess && (
        <Alert
          severity="success"
          onClose={() => setAddToPlanSuccess(null)}
          sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, minWidth: 300 }}
        >
          {addToPlanSuccess}
        </Alert>
      )}

      {/* Add to Plan Dialog */}
      <Dialog open={addToPlanOpen} onClose={() => setAddToPlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Meal Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {recipe?.name}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Meal Type</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {['breakfast', 'lunch', 'dinner'].map(type => (
              <Chip
                key={type}
                label={type.charAt(0).toUpperCase() + type.slice(1)}
                onClick={() => setAddToPlanMealType(type)}
                color={addToPlanMealType === type ? 'primary' : 'default'}
                variant={addToPlanMealType === type ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Date</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {getDateOptions().map(opt => (
              <Chip
                key={opt.date}
                label={opt.label}
                onClick={() => setAddToPlanDate(opt.date)}
                color={addToPlanDate === opt.date ? 'primary' : 'default'}
                variant={addToPlanDate === opt.date ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            Servings will be automatically set based on your family's meal schedule.
          </Typography>

          {addToPlanError && (
            <Alert severity="error" sx={{ mt: 2 }}>{addToPlanError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToPlanOpen(false)} disabled={addingToPlan}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToPlan}
            disabled={!addToPlanDate || addingToPlan}
          >
            {addingToPlan ? <CircularProgress size={20} /> : 'Add to Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecipeDetailScreen;